import { Component, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, inject, ChangeDetectionStrategy } from '@angular/core';

import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisCalendarDefaultsDirective } from '../../core/directive/his-calendar.directive';
import { HisInputWidget } from '../model/his-widget-input';

/**
 * The calendar component used by the generic search.
 *
 * The value of a calendar component is stored in widget.value.
 * We have to store it as a string, because the generic search backend can not handle Date objects,
 * or at least not in the (UTC) format provided by p-calendar.
 *
 * On the other hand, p-calendar obliges us to store the string-values in the format matching the current locale...
 */
@Component( {
    selector: 'his-search-calendar',
    templateUrl: './his-search-calendar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchCalendarComponent implements OnInit, AfterViewInit {
    hisBaseGlobalsService = inject(HisBaseGlobalsService);

    // It is not possible to search with operators at p-calendar
    // So we decided to use in the first step pInputText
    // and in the next step to add a datepicker in any way

    // @Input() widget: HisCalendarWidget; // see #259765
    @Input() widget: HisInputWidget;

    // true if this is the first input element in the search mask
    @Input() isFirstInputElement = false;

    @ViewChild( 'inputElement' ) inputElement: ElementRef;

    @Output() changed = new EventEmitter<string>();

    @Output() inputChanged = new EventEmitter<string>(); // type matches widget.value type

    calendarValue: string;

    ngOnInit() {
        let value = this.widget.value;
        if ( value ) {
            // After a language change we can still get a value here but in the wrong date format
            console.log( 'HisSearchCalendarComponent: Current date widget.value =', this.widget.value );
            // Unfortunately in that case we do not know the previous date format, so we have to guess it.
            // This will work for german and english, but cannot be guaranteed for other locales.
            let dd: string;
            let mm: string;
            let yyyy: string;
            let lang = this.hisBaseGlobalsService.getLanguage();
            if ( value.indexOf( '/' ) >= 0 && lang === 'de' ) {
                // the value is still in english date format, but the locale changed to german
                let p1: number = value.indexOf( '/' );
                mm = value.substring( 0, p1 );
                let p2: number = value.indexOf( '/', p1 + 1 );
                dd = value.substring( p1 + 1, p2 );
                yyyy = value.substring( p2 + 1 );
            } else if ( value.indexOf( '.' ) >= 0 && lang === 'en' ) {
                // the value is still in german date format, but the locale changed to english
                let p1: number = value.indexOf( '.' );
                dd = value.substring( 0, p1 );
                let p2: number = value.indexOf( '.', p1 + 1 );
                mm = value.substring( p1 + 1, p2 );
                yyyy = value.substring( p2 + 1 );
            }
            if ( dd && mm && yyyy ) {
                let dateFormat = HisCalendarDefaultsDirective.getDateFormat( lang );
                let dateStr = dateFormat.replace( 'dd', dd ).replace( 'mm', mm ).replace( 'yy', yyyy );
                console.log( 'HisSearchCalendarComponent: Changed date to ', dateStr );
                this.widget.value = dateStr;
            }
        }
    }

    ngAfterViewInit() {
        // Set focus on first input element
        if ( this.isFirstInputElement ) {
            setTimeout( () => this.inputElement.nativeElement.focus() );
        }
    }

    getHtmlFocusElementId(): string {
        return this.widget.uid;
    }

    inputChange( changes: string ) {
        console.log( 'HisSearchCalendarComponent.inputChange(): Emit event', changes );
        this.changed.emit( changes );
    }

    calendarChange() {
        console.log( 'Received date change event for widget', this.widget.id, '. Re-emit new date value', this.calendarValue );
        console.log( 'his-search-calendar.component#calendarChange() #this.calendarValue', this.calendarValue );

        if ( !this.calendarValue && this.widget.value ) {
            this.calendarValue = this.widget.value;
        }

        this.widget.value = this.calendarValue;
        this.changed.emit( this.calendarValue );
    }
}
